import React, { useEffect, useRef, useCallback } from 'react';
import Paper, { PaperProps } from '@mui/material/Paper';

interface WaterCardProps extends PaperProps {
  shimmerDelay?: number;
  disableEffects?: boolean;
}

const WaterCard: React.FC<WaterCardProps> = ({
  shimmerDelay = 0,
  disableEffects = false,
  children,
  sx,
  ...paperProps
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const bubbleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reducedMotion = useRef(false);

  // Spawn a single bubble inside the card
  const spawnBubble = useCallback(() => {
    const card = cardRef.current;
    if (!card || reducedMotion.current || disableEffects) return;

    const bubble = document.createElement('div');
    bubble.className = 'card-bubble';
    const size = 4 + Math.random() * 12;
    const left = 10 + Math.random() * 80;
    const duration = 4 + Math.random() * 6;
    const driftX = (Math.random() - 0.5) * 30;

    bubble.style.width = size + 'px';
    bubble.style.height = size + 'px';
    bubble.style.left = left + '%';
    bubble.style.bottom = '-' + size + 'px';
    bubble.style.setProperty('--drift-x', driftX + 'px');
    bubble.style.animationDuration = duration + 's';

    card.appendChild(bubble);
    bubble.addEventListener('animationend', () => bubble.remove());
  }, [disableEffects]);

  useEffect(() => {
    reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (disableEffects || reducedMotion.current) return;

    // Initial burst of 2-3 bubbles
    const burstCount = 2 + Math.floor(Math.random() * 2);
    const burstTimers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < burstCount; i++) {
      burstTimers.push(setTimeout(spawnBubble, Math.random() * 3000));
    }

    // Ongoing bubble spawning
    const interval = 2500 + Math.random() * 3000;
    bubbleTimerRef.current = setInterval(() => {
      if (document.hidden) return;
      spawnBubble();
    }, interval);

    return () => {
      burstTimers.forEach(clearTimeout);
      if (bubbleTimerRef.current) clearInterval(bubbleTimerRef.current);
    };
  }, [disableEffects, spawnBubble]);

  // Click ripple handler
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (disableEffects || reducedMotion.current) return;

    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ripple = document.createElement('div');
    ripple.className = 'card-ripple';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';
    card.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove());
  }, [disableEffects]);

  return (
    <Paper
      ref={cardRef}
      className="water-card"
      data-shimmer-delay={shimmerDelay}
      onClick={handleClick}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
      {...paperProps}
    >
      {children}
    </Paper>
  );
};

export default WaterCard;
